import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DataListTableElement, DLT_DATA_TYPES } from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';
import { interval, map, startWith, Subject, takeUntil } from 'rxjs';

import {
  AttachmentStatus,
  CHIP_BACKGROUND_COLORS,
  FILE_TYPES,
  FORM_BUILDER_ELEMENT_TYPES,
  STAGE_ROUTING_STATUS,
} from '@shared/constants';
import {
  IAddressField,
  IAttachmentDocument,
  IBuilderDate,
  IBuilderDateTime,
  IBuilderTime,
  ICheckboxField,
  IContactEmail,
  IContactNumber,
  IContactNumberField,
  IDropdownField,
  IEmailField,
  IName,
  INameField,
  INodeFormSavePayload,
  INumberField,
  IParagraphField,
  IRadioButtonField,
  //   IRequestDocument,
  IRequestDocumentDocumentInfo,
  IShortAnswerField,
  IUploadFileToDMS,
} from '@shared/interfaces';
import { generateFileTypeRegex } from '@shared/utils';

import { FilesService } from '../../../../../services/files.service';
import {
  SnackbarService,
  SUCCESS_TYPES,
} from '../../../../../services/snackbar.service';
import { VirtualModuleFormBuilderService } from '../../service/virtual-module-form-builder.service';

// Constants for document actions (following create-thread pattern)
const ACTIONS = {
  DOWNLOAD: 'DOWNLOAD',
  DELETE: 'DELETE',
};

export interface IDocumentUploadData {
  [key: string]: {
    documentName: string;
    identity_id: string;
    dataList: DataListTableElement[];
    toBeUploadedData: IUploadFileToDMS<File>[];
    toBeUploadedDocuments: IAttachmentDocument[];
    toBeDeleted: IAttachmentDocument[];
  };
}

export interface IFormFieldDefinition {
  type: string;
  formControlName: string;
  title?: string;
  subTitle?: string;
  label?: string;
  maximumFileSize?: string;
  isPDFSelected?: boolean;
  isDOCXSelected?: boolean;
  isJPGSelected?: boolean;
  isPNGSelected?: boolean;
  isEMLSelected?: boolean;
  isSVGSelected?: boolean;
  isCSVSelected?: boolean;
  isXLSSelected?: boolean;
  isRequired?: boolean;
  isExternalCollection?: boolean;
  externalLink?: boolean;
  placeholder?: string;
  options?: { value: string; isSelected?: boolean }[];
}

export interface IFormFieldResponse {
  formField: IFormFieldDefinition;
  formValue: IRequestDocumentDocumentInfo[];
  nodeInfo: {
    nodeId: string;
    nodeName: string;
    nodeType: string;
  };
  workflowId: string;
}

export interface IEnrichedFormItem {
  value: {
    formControlName: string;
    value: unknown;
  };
  _id: string;
  fieldDefinition: IFormFieldDefinition | null;
  nodeInfo: {
    nodeId: string;
    nodeName: string;
    nodeType: string;
  } | null;
  formControl: FormControl;
  enrichedData: {
    formControlName: string;
    actualValue: unknown;
    title: string;
    subTitle: string;
    label: string;
    type: string;
    isExternalCollection: boolean;
    isRequired: boolean;
    options: { value: string; isSelected?: boolean }[];
    placeholder: string;
    value?: unknown;
    maximum_file_size: number;
    allowed_file_types: string[];
    isMultipleUpload: boolean;
    // Additional properties required by form field components
    showInDLT: boolean;
    isSearchFilter: boolean;
  };
}

@Component({
  selector: 'base-request-document-information-collection',
  templateUrl: './request-document-information-collection.component.html',
  styleUrls: ['./request-document-information-collection.component.scss'],
  standalone: false,
})
export class RequestDocumentInformationCollectionPageComponent
  implements OnDestroy, OnInit
{
  private readonly destroy$ = new Subject<void>();
  workflowId = '';
  virtualModuleId = '';
  requestDocId: string;
  externalToken: string | null = null; // Token for external user authentication

  userDisplayName = 'Mr. John David';
  supportPhone = '+94 71 234 5678';
  supportEmail = 'support@email.com';
  helpUrl = 'https://www.example.com/support';

  // set your deadline here
  private readonly deadline = new Date(Date.now() + 10 * 60 * 60 * 1000); // +10 hours
  countdown$ = interval(1000).pipe(
    startWith(0),
    map(() => {
      const ms = Math.max(0, this.deadline.getTime() - Date.now());
      const h = Math.floor(ms / 36e5)
        .toString()
        .padStart(2, '0');
      const m = Math.floor((ms % 36e5) / 6e4)
        .toString()
        .padStart(2, '0');
      const s = Math.floor((ms % 6e4) / 1000)
        .toString()
        .padStart(2, '0');
      return `${h}:${m}:${s}`;
    })
  );

  isLoading = true;
  isSubmitting = false;
  toBeUploadedData: IUploadFileToDMS<File>[] = [];
  toBeDeletedData: any[] = [];
  private hasInitialized = false;

  // Dynamic document upload data
  documentUploadData: IDocumentUploadData = {};

  // Store form field definitions
  formFieldDefinitions: Map<string, IFormFieldResponse> = new Map();
  virtualModuleData: INodeFormSavePayload | null = null;

  // Store enriched form data with controls
  enrichedFormData: IEnrichedFormItem[] = [];
  private isInitializingUploads = false; // Prevent recursive initialization

  // Form for any additional data (if needed)
  documentCollectionFormData = new FormGroup({
    additionalNotes: new FormControl(''),
  });

  // Import required types and constants
  FORM_BUILDER_ELEMENT_TYPES = FORM_BUILDER_ELEMENT_TYPES;

  // Type-safe adapters for form field components
  getShortAnswerData(enrichedData: any): IShortAnswerField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.SHORT_ANSWER,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getParagraphData(enrichedData: any): IParagraphField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.PARAGRAPH,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getCheckboxData(enrichedData: any): ICheckboxField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.CHECKBOX,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      options: enrichedData.options || [],
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getRadioButtonData(enrichedData: any): IRadioButtonField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.RADIO_BUTTON,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      options: enrichedData.options || [],
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getDropdownData(enrichedData: any): IDropdownField {
    const fieldDefinition = this.getFormFieldDefinition(
      enrichedData.formControlName
    );

    // If form field definition is not loaded yet, return a placeholder
    if (!fieldDefinition) {
      console.warn(
        'getDropdownData: Form field definition not loaded yet for',
        enrichedData.formControlName
      );
      return {
        type: FORM_BUILDER_ELEMENT_TYPES.DROPDOWN,
        formControlName: enrichedData.formControlName,
        label: enrichedData.label,
        options: [],
        isRequired: enrichedData.isRequired,
        showInDLT: enrichedData.showInDLT,
        isExternalCollection: enrichedData.isExternalCollection,
        isSearchFilter: enrichedData.isSearchFilter,
        valueSource: 'internal',
        internalOptions: [],
      } as IDropdownField;
    }

    const formField = fieldDefinition?.formField as any;

    // Check if this is an external reference dropdown
    const isExternalReference =
      formField?.valueSource === 'external_references' ||
      (formField?.externalCategory && formField?.externalField);

    if (isExternalReference) {
      return {
        type: FORM_BUILDER_ELEMENT_TYPES.DROPDOWN,
        formControlName: enrichedData.formControlName,
        label: enrichedData.label,
        options: enrichedData.options || [],
        isRequired: enrichedData.isRequired,
        showInDLT: enrichedData.showInDLT,
        isExternalCollection: enrichedData.isExternalCollection,
        isSearchFilter: enrichedData.isSearchFilter,
        valueSource: 'external_references' as const,
        externalCategory: formField?.externalCategory || '',
        externalField: formField?.externalField || '',
      } as IDropdownField;
    }

    return {
      type: FORM_BUILDER_ELEMENT_TYPES.DROPDOWN,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      options: enrichedData.options || [],
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
      valueSource: 'internal',
      internalOptions: enrichedData.options || [],
    } as IDropdownField;
  }

  getDateTimeData(enrichedData: any): IBuilderDateTime {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.DATE_TIME,
      formControlName: enrichedData.formControlName,
      label: enrichedData.title || enrichedData.label,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
      disallowCurrentTime: false,
      isPastTimeSelected: false,
      isFutureTimeSelected: false,
      disallowCurrentDate: false,
      isPastDateSelected: false,
      isFutureDateSelected: false,
      valueSource: 'datetime',
    } as IBuilderDateTime;
  }

  getDateData(enrichedData: any): IBuilderDate {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.DATE,
      formControlName: enrichedData.formControlName,
      label: enrichedData.title || enrichedData.label,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
      disallowCurrentDate: false,
      isPastDateSelected: false,
      isFutureDateSelected: false,
      valueSource: 'date',
    } as IBuilderDate;
  }

  getTimeData(enrichedData: any): IBuilderTime {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.TIME,
      formControlName: enrichedData.formControlName,
      label: enrichedData.title || enrichedData.label,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
      disallowCurrentTime: false,
      isPastTimeSelected: false,
      isFutureTimeSelected: false,
      valueSource: 'time',
    } as IBuilderTime;
  }

  getEmailData(enrichedData: any): IEmailField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.EMAIL,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getNumberData(enrichedData: any): INumberField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.NUMBER,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
      valueType: 'number',
      isReadOnly: false,
    };
  }

  getContactNumberData(enrichedData: any): IContactNumberField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.CONTACT_NUMBER,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getNameData(enrichedData: any): INameField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.NAME,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  getAddressData(enrichedData: any): IAddressField {
    return {
      type: FORM_BUILDER_ELEMENT_TYPES.ADDRESS,
      formControlName: enrichedData.formControlName,
      label: enrichedData.label,
      placeholder: enrichedData.placeholder,
      isRequired: enrichedData.isRequired,
      showInDLT: enrichedData.showInDLT,
      isExternalCollection: enrichedData.isExternalCollection,
      isSearchFilter: enrichedData.isSearchFilter,
    };
  }

  // Helper method to get properly typed documents array for DOCUMENT_REQUEST
  getDocumentRequestDocuments(enrichedData: any): any[] {
    const value = enrichedData.value;
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }

  constructor(
    private readonly virtualModuleFormBuilderService: VirtualModuleFormBuilderService,
    private readonly router: Router,
    private readonly snackbar: SnackbarService,
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly filesService: FilesService
  ) {
    this.virtualModuleId = this.route.snapshot.paramMap.get('id') || '';
    this.requestDocId = this.virtualModuleId; // Using same ID for now

    this.workflowId =
      this.route.snapshot.queryParamMap.get('workflowId') ||
      this.route.snapshot.queryParams['workflowId'] ||
      '';

    // Extract token from query params for external user authentication (initial snapshot)
    this.externalToken =
      this.route.snapshot.queryParamMap.get('token') ||
      this.route.snapshot.queryParams['token'] ||
      null;
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['id']) {
        this.virtualModuleId = params['id'];
        this.requestDocId = this.virtualModuleId;
      }
    });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        if (queryParams['workflowId']) {
          this.workflowId = queryParams['workflowId'];
        }

        // Extract token from query params for external user authentication
        if (queryParams['token']) {
          this.externalToken = queryParams['token'];
        }

        if (this.virtualModuleId && this.workflowId) {
          this.getRequestDocumentData();
        }
      });

    // Fallback: If we already have both parameters from constructor
    if (this.virtualModuleId && this.workflowId) {
      this.getRequestDocumentData();
    } else {
      // Don't show error immediately, wait a bit for route subscriptions
      setTimeout(() => {
        if (!this.virtualModuleId || !this.workflowId) {
          this.isLoading = false;
          this.snackbar.error(
            'Missing required parameters. Virtual Module ID or Workflow ID not found in URL.'
          );
        }
      }, 1000);
    }
  }

  getRequestDocumentData() {
    // Prevent duplicate initialization
    if (this.hasInitialized) {
      return;
    }

    // Validate that we have both required parameters
    if (!this.workflowId || !this.virtualModuleId) {
      this.isLoading = false;
      return;
    }

    this.hasInitialized = true;
    this.virtualModuleFormBuilderService
      .getPublicVirtualModuleDataById(this.virtualModuleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) {
            this.virtualModuleData = data.data;

            // Fetch form field definitions for each formControlName
            this.fetchFormFieldDefinitions();

            // Update form controls state after data is loaded
            setTimeout(() => {
              this.updateFormControlsState();
            }, 0);
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  fetchFormFieldDefinitions() {
    if (!this.virtualModuleData?.value) {
      return;
    }

    // Extract all formControlNames from the virtual module data
    const formControlNames = this.virtualModuleData.value
      .map((item: any) => item.value?.formControlName)
      .filter((name: string) => name); // Filter out null/undefined

    if (formControlNames.length === 0) {
      return;
    }

    let completedRequests = 0;
    const totalRequests = formControlNames.length;

    // Fetch form field definitions for each formControlName
    formControlNames.forEach((formControlName: string) => {
      this.virtualModuleFormBuilderService
        .getFormFieldByControlName(
          this.workflowId,
          this.virtualModuleId,
          formControlName
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            completedRequests++;

            if (response.data?.formField) {
              this.formFieldDefinitions.set(formControlName, response.data);
            }

            if (response.data?.formValue) {
              // Process uploaded documents from formValue
              this.processFormValueDocuments(
                response.data.formValue,
                formControlName
              );
            }

            if (completedRequests === totalRequests) {
              // Clear enriched form data to force re-creation with updated field definitions
              this.enrichedFormData = [];
              // Trigger change detection to re-render with correct dropdown types
              setTimeout(() => {
                // Force change detection by accessing getEnrichedValueData
                this.getEnrichedValueData();
              }, 0);
            }
          },
          error: () => {
            completedRequests++;

            if (completedRequests === totalRequests) {
              this.enrichedFormData = [];
            }
          },
        });
    });
  }

  patchDocumentData(document: any): any {
    return {
      ...document,
      status: document.status || 'PENDING',
      files: (document.files || []).map((file: any) => ({
        type: file.type || 'unknown',
        status: file.status || 'PENDING',
        file_id: file.file_id || '',
        name: file.name || '',
        uploaded_date: file.uploaded_date || new Date().toISOString(),
      })),
    };
  }

  // Helper method to get form field definition by formControlName
  private getFormFieldDefinition(
    formControlName: string
  ): IFormFieldResponse | undefined {
    return this.formFieldDefinitions.get(formControlName);
  }

  // Process uploaded documents from formValue response
  private processFormValueDocuments(
    formValue: any[],
    formControlName: string
  ): void {
    if (!Array.isArray(formValue)) {
      return;
    }

    formValue.forEach((documentConfig, docIndex) => {
      if (documentConfig.files && Array.isArray(documentConfig.files)) {
        const sectionKey = `DOC_REQ_${formControlName}_${docIndex}`;

        // Initialize document upload data for this section if it doesn't exist
        if (!this.documentUploadData[sectionKey]) {
          this.documentUploadData[sectionKey] = {
            documentName: documentConfig.name || `Document ${docIndex + 1}`,
            identity_id:
              documentConfig.identity_id ||
              `doc_req_${formControlName}_${docIndex}`,
            dataList: [],
            toBeUploadedData: [],
            toBeUploadedDocuments: [],
            toBeDeleted: [],
          };
        }

        // Process each uploaded file
        documentConfig.files.forEach((file: any) => {
          if (file.file_id) {
            // Check if this file is already processed to prevent duplicates
            const existingFile = this.documentUploadData[
              sectionKey
            ].toBeUploadedDocuments.find((doc) => doc.file_id === file.file_id);

            if (existingFile) {
              return; // Skip if already processed
            }

            // Create document object for data list table
            // Preserve the original status from the server (UPLOADED, APPROVED, REJECTED, etc.)
            const document: IAttachmentDocument = {
              name: file.name,
              uploaded_date: file.uploaded_date
                ? new Date(file.uploaded_date)
                : new Date(),
              type: file.type || 'unknown',
              status: file.status || AttachmentStatus.UPLOADED,
              file_id: file.file_id,
            };

            // Add to toBeUploadedDocuments array
            this.documentUploadData[sectionKey].toBeUploadedDocuments.push(
              document
            );

            // Create data list table element
            this.updateDataListTableElement(
              this.documentUploadData[sectionKey].dataList.length,
              document,
              sectionKey
            );
          }
        });
      }
    });
  }

  // Helper method to get enriched value data with form field definition
  getEnrichedValueData(): IEnrichedFormItem[] {
    if (!this.virtualModuleData?.value) {
      return [];
    }

    // Create enriched data with form controls if not already created
    if (this.enrichedFormData.length === 0) {
      this.enrichedFormData = this.virtualModuleData.value
        .map((item) => {
          const formControlName = item.value?.formControlName;
          const fieldDefinition = this.getFormFieldDefinition(formControlName);

          // Only include items that have form field definitions with isExternalCollection=true
          if (!fieldDefinition?.formField?.isExternalCollection) {
            return null;
          }

          // Create form control with current value and validators
          // For DOCUMENT_REQUEST fields, use formValue from field definition if available
          let currentValue = item.value?.value;
          if (
            fieldDefinition.formField.type === 'DOCUMENT_REQUEST' &&
            fieldDefinition.formValue
          ) {
            currentValue = fieldDefinition.formValue;
          }
          const formControl = new FormControl(currentValue);

          // Add required validator if field is required
          if (fieldDefinition.formField.isRequired) {
            formControl.setValidators([Validators.required]);
            formControl.updateValueAndValidity();
          }

          // Get allowed file types based on field definition
          const getAllowedFileTypes = (
            field: IFormFieldDefinition
          ): string[] => {
            const types: string[] = [];
            if (field.isPDFSelected) types.push('PDF');
            if (field.isDOCXSelected) types.push('DOCX');
            if (field.isJPGSelected) types.push('JPG');
            if (field.isPNGSelected) types.push('PNG');
            if (field.isEMLSelected) types.push('EML');
            if (field.isSVGSelected) types.push('SVG');
            if (field.isCSVSelected) types.push('CSV');
            if (field.isXLSSelected) types.push('XLS');
            return types.length > 0 ? types : ['PDF']; // Default to PDF if none selected
          };

          const enrichedItem: IEnrichedFormItem = {
            ...item,
            _id: formControlName + '_' + Date.now(), // Generate unique ID
            fieldDefinition: fieldDefinition.formField,
            nodeInfo: fieldDefinition.nodeInfo,
            formControl,
            enrichedData: {
              formControlName,
              actualValue: currentValue,
              title:
                fieldDefinition.formField.title ||
                fieldDefinition.formField.label ||
                'Untitled Field',
              subTitle: fieldDefinition.formField.subTitle || '',
              label:
                fieldDefinition.formField.title ||
                fieldDefinition.formField.label ||
                formControlName,
              type: fieldDefinition.formField.type || 'UNKNOWN',
              isExternalCollection:
                fieldDefinition.formField.isExternalCollection || false,
              isRequired: fieldDefinition.formField.isRequired || false,
              options: fieldDefinition.formField.options || [],
              placeholder: fieldDefinition.formField.placeholder || '',
              value: currentValue,
              maximum_file_size: parseInt(
                fieldDefinition.formField.maximumFileSize || '5'
              ),
              allowed_file_types: getAllowedFileTypes(
                fieldDefinition.formField
              ),
              isMultipleUpload: true, // Default to true for now, can be configured later
              // Add missing properties that form field components expect
              showInDLT: false,
              isSearchFilter: false,
            },
            value: {
              formControlName,
              value: currentValue,
            },
          };

          return enrichedItem;
        })
        .filter((item): item is IEnrichedFormItem => item !== null);

      // Initialize document upload data for form fields after creating enriched data
      // Only initialize if not already initializing to prevent recursion
      if (!this.isInitializingUploads) {
        this.isInitializingUploads = true;
        this.initializeFormFieldDocumentUploads();
        this.isInitializingUploads = false;
      }

      // Update form controls state based on areButtonsDisabled
      this.updateFormControlsState();
    }

    // Update form controls state when enriched data already exists
    if (this.enrichedFormData.length > 0) {
      this.updateFormControlsState();
    }

    return this.enrichedFormData;
  }

  // Track by function for ngFor performance
  trackByFormControlName(index: number, item: IEnrichedFormItem): string {
    return item.enrichedData?.formControlName || index.toString();
  }

  // Update form controls state based on areButtonsDisabled
  updateFormControlsState(): void {
    const shouldDisable = this.areButtonsDisabled;
    this.enrichedFormData.forEach((item) => {
      if (item.formControl) {
        if (shouldDisable) {
          item.formControl.disable({ emitEvent: false });
        } else {
          item.formControl.enable({ emitEvent: false });
        }
      }
    });
  }

  // Build regex accepted by drag-and-drop from saved allowed_file_types
  getAllowedTypesRegex(allowed: string[] | undefined): RegExp {
    const fileTypes: FILE_TYPES[] = [];

    (allowed || []).forEach((t) => {
      const type = (t || '').toUpperCase();

      switch (true) {
        case ['PDF'].includes(type):
          fileTypes.push(FILE_TYPES.PDF);
          break;
        case ['DOC', 'DOCX', 'WORD'].includes(type):
          fileTypes.push(FILE_TYPES.DOCS);
          break;
        case ['XLS', 'XLSX', 'EXCEL'].includes(type):
          fileTypes.push(FILE_TYPES.EXCEL);
          break;
        case ['CSV'].includes(type):
          fileTypes.push(FILE_TYPES.CSV);
          break;
        case ['EML'].includes(type):
          fileTypes.push(FILE_TYPES.EML);
          break;
        case ['RTF'].includes(type):
          fileTypes.push(FILE_TYPES.RTF);
          break;
        case ['TXT', 'TEXT'].includes(type):
          fileTypes.push(FILE_TYPES.TEXT);
          break;
        case ['PPT', 'PPTX'].includes(type):
          fileTypes.push(FILE_TYPES.PPT);
          break;
        // Any image variations
        case [
          'JPG',
          'JPEG',
          'JPE|JPG',
          'PNG',
          'SVG',
          'BMP',
          'GIF',
          'WEBP',
          'TIFF',
        ].includes(type):
          fileTypes.push(FILE_TYPES.IMAGE);
          break;
        default:
          // Fallback to binary to avoid accidental rejection
          break;
      }
    });

    // Default to IMAGE+PDF if unspecified to keep previous behavior minimal
    const effective = fileTypes.length
      ? fileTypes
      : [FILE_TYPES.IMAGE, FILE_TYPES.PDF];
    return generateFileTypeRegex(effective);
  }

  // Build friendly names for banner text in drag-and-drop
  getAllowedTypesNames(allowed: string[] | undefined): string {
    if (!allowed?.length) return 'images, pdf';
    return allowed.join(', ').toLowerCase();
  }

  // Handle form field value changes
  onFormFieldValueChange(
    value: unknown,
    enrichedItem: IEnrichedFormItem
  ): void {
    // Update the form control value
    if (enrichedItem.formControl) {
      enrichedItem.formControl.setValue(value, { emitEvent: false });
    }

    // Update the underlying data
    if (enrichedItem.value?.value !== undefined) {
      enrichedItem.value.value = value;
    }

    // Update the virtual module data
    if (this.virtualModuleData?.value) {
      const virtualDataIndex = this.virtualModuleData.value.findIndex(
        (item) =>
          item.value?.formControlName ===
          enrichedItem.enrichedData.formControlName
      );

      if (virtualDataIndex !== -1) {
        this.virtualModuleData.value[virtualDataIndex].value.value = value as
          | string
          | IContactNumber[]
          | IContactEmail[]
          | IName
          | Date
          | string[];
      }
    }
  }

  // Initialize upload data for form field document uploads
  initializeFormFieldDocumentUploads(): void {
    // Use this.enrichedFormData directly instead of calling getEnrichedValueData()
    // to prevent infinite recursion
    this.enrichedFormData.forEach((enrichedItem) => {
      const fieldType = enrichedItem.enrichedData.type;
      const formControlName = enrichedItem.enrichedData.formControlName;

      if (fieldType === 'UPLOAD_FIELD') {
        const sectionKey = `UPLOAD_FIELD_${formControlName}`;

        if (!this.documentUploadData[sectionKey]) {
          this.documentUploadData[sectionKey] = {
            documentName: enrichedItem.enrichedData.title || 'Upload Field',
            identity_id: `upload_field_${formControlName}`,
            dataList: [],
            toBeUploadedData: [],
            toBeUploadedDocuments: [],
            toBeDeleted: [],
          };
        }
      } else if (
        fieldType === 'DOCUMENT_REQUEST' &&
        enrichedItem.enrichedData.value
      ) {
        // For DOCUMENT_REQUEST, the value should be an array of document configurations
        const documentConfigs = Array.isArray(enrichedItem.enrichedData.value)
          ? enrichedItem.enrichedData.value
          : [];

        documentConfigs.forEach(
          (
            document: { name?: string; identity_id?: string },
            docIndex: number
          ) => {
            const sectionKey = `DOC_REQ_${formControlName}_${docIndex}`;

            if (!this.documentUploadData[sectionKey]) {
              this.documentUploadData[sectionKey] = {
                documentName:
                  document.name ||
                  `${enrichedItem.enrichedData.title} - Document ${
                    docIndex + 1
                  }`,
                identity_id:
                  document.identity_id ||
                  `doc_req_${formControlName}_${docIndex}`,
                dataList: [],
                toBeUploadedData: [],
                toBeUploadedDocuments: [],
                toBeDeleted: [],
              };
            }
          }
        );
      }
    });
  }

  // Handle file uploads for form fields (UPLOAD_FIELD and DOCUMENT_REQUEST)
  private handleFormFieldFileUpload(file: File, sectionKey: string): void {
    // Get validation rules based on field type and section
    let validationRules: {
      maximum_file_size: number;
      allowed_file_types: string[];
      isMultipleUpload: boolean;
    } = {
      maximum_file_size: 5, // Default 5MB
      allowed_file_types: ['PDF', 'DOC', 'DOCX', 'JPG', 'PNG'], // Default allowed types
      isMultipleUpload: true, // Default allow multiple
    };

    // Extract validation rules from field definitions
    if (sectionKey.startsWith('UPLOAD_FIELD_')) {
      const formControlName = sectionKey.replace('UPLOAD_FIELD_', '');
      const enrichedItem = this.getEnrichedValueData().find(
        (item) => item.enrichedData.formControlName === formControlName
      );

      if (enrichedItem?.enrichedData) {
        validationRules = {
          maximum_file_size: enrichedItem.enrichedData.maximum_file_size || 5,
          allowed_file_types: enrichedItem.enrichedData.allowed_file_types || [
            'PDF',
            'DOC',
            'DOCX',
          ],
          isMultipleUpload:
            enrichedItem.enrichedData.isMultipleUpload !== false,
        };
      }
    } else if (sectionKey.startsWith('DOC_REQ_')) {
      // For DOCUMENT_REQUEST, use the field's global settings since the API response
      // doesn't provide per-document configuration in the current structure
      const parts = sectionKey.replace('DOC_REQ_', '').split('_');
      parts.pop(); // Remove the docIndex part
      const formControlName = parts.join('_');

      const enrichedItem = this.getEnrichedValueData().find(
        (item) => item.enrichedData.formControlName === formControlName
      );

      if (enrichedItem?.enrichedData) {
        validationRules = {
          maximum_file_size: enrichedItem.enrichedData.maximum_file_size || 5,
          allowed_file_types: enrichedItem.enrichedData.allowed_file_types || [
            'PDF',
          ],
          isMultipleUpload:
            enrichedItem.enrichedData.isMultipleUpload !== false,
        };
      }
    }

    // Validate file size
    if (file.size > validationRules.maximum_file_size * 1024 * 1024) {
      this.snackbar.error(
        `File size exceeds limit of ${validationRules.maximum_file_size}MB`
      );
      return;
    }

    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toUpperCase();
    const allowedTypes = validationRules.allowed_file_types.map(
      (type: string) => type.toUpperCase()
    );

    if (!allowedTypes.includes(fileExtension || '')) {
      this.snackbar.error(
        `File type not allowed. Allowed types: ${validationRules.allowed_file_types.join(
          ', '
        )}`
      );
      return;
    }

    // Check if multiple upload is allowed
    const uploadData = this.documentUploadData[sectionKey];
    if (!validationRules.isMultipleUpload && uploadData?.dataList?.length > 0) {
      this.snackbar.error('Only one file is allowed for this field');
      return;
    }

    // Create document object like in create-thread component
    const document: IAttachmentDocument = {
      name: file.name,
      uploaded_date: new Date(),
      type: fileExtension?.toLowerCase() || 'unknown',
      status: AttachmentStatus.UPLOADING,
    };

    this.saveDocument(document, file, sectionKey);
  }

  // Collect all form values for submission
  collectFormValues(): Record<string, unknown> {
    const formValues: Record<string, unknown> = {};

    this.getEnrichedValueData().forEach((item) => {
      const formControlName = item.enrichedData.formControlName;
      if (formControlName && item.formControl) {
        const value = item.formControl.value;
        formValues[formControlName] = value;
      }
    });

    return formValues;
  }

  // Prepare complete submission data structure
  prepareSubmissionData(status: string): {
    formData: {
      documents: {
        documentName: string;
        identity_id: string;
        sectionKey: string;
        files: IAttachmentDocument[];
      }[];
      formFieldDocuments: {
        documentName: string;
        identity_id: string;
        sectionKey: string;
        files: IAttachmentDocument[];
        fieldType: string;
        formControlName: string;
      }[];
      formValues: Record<string, unknown>;
    };
    metadata: {
      submissionType: string;
      timestamp: string;
      requestDocId: string;
      workflowId: string;
      virtualModuleId: string;
      userAgent: string;
      submittedBy: string;
    };
    summary: {
      totalFormFields: number;
      completedFormFields: number;
      totalDocuments: number;
      uploadedDocuments: number;
      documentSections: number;
      regularDocuments: number;
      formFieldDocuments: number;
      submissionType: string;
      hasFormData: boolean;
      hasDocuments: boolean;
      isComplete: boolean;
    };
  } {
    // Collect documents data from all sections (including form field uploads)
    const uploadedDocuments = [];
    // this.virtualModuleData.value.filter((item) => {
    //   (item.value?.value as IRequestDocumentDocumentInfo).files === DOCUMENT_STATUS.;
    // });
    const formFieldDocuments = []; // Separate collection for form field documents
    let totalDocuments = 0;
    let uploadedCount = 0;

    for (const sectionKey of Object.keys(this.documentUploadData)) {
      const section = this.documentUploadData[sectionKey];

      // Persist any document that is already in DMS (has a file_id),
      // keeping the current status (UPLOADED/APPROVED/REJECTED/RETURNED, etc.)
      // Exclude documents marked for deletion
      const successfulDocs =
        section.toBeUploadedDocuments?.filter(
          (doc) =>
            !!doc.file_id &&
            doc.status !== AttachmentStatus.PENDING_DELETE &&
            doc.status !== AttachmentStatus.FAILED &&
            doc.status !== AttachmentStatus.UPLOADING &&
            doc.status !== AttachmentStatus.PENDING_UPLOAD
        ) || [];

      totalDocuments += section.toBeUploadedDocuments?.length || 0;
      uploadedCount += successfulDocs.length;

      if (successfulDocs.length > 0) {
        const documentData = {
          documentName: section.documentName,
          identity_id: section.identity_id,
          sectionKey,
          files: successfulDocs,
        };

        // Categorize documents by type
        if (
          sectionKey.startsWith('UPLOAD_FIELD_') ||
          sectionKey.startsWith('DOC_REQ_')
        ) {
          // This is a form field document
          formFieldDocuments.push({
            ...documentData,
            fieldType: sectionKey.startsWith('UPLOAD_FIELD_')
              ? 'UPLOAD_FIELD'
              : 'DOCUMENT_REQUEST',
            formControlName: sectionKey.startsWith('UPLOAD_FIELD_')
              ? sectionKey.replace('UPLOAD_FIELD_', '')
              : sectionKey.split('_').slice(2, -1).join('_'), // Extract formControlName from DOC_REQ_
          });
        } else {
          // This is a regular document section
          uploadedDocuments.push(documentData);
        }
      }
    }

    // Collect form field values
    const formFieldValues = this.collectFormValues();

    // Prepare main form data
    const formData = {
      documents: uploadedDocuments, // Regular document sections
      formFieldDocuments, // Documents from form fields (UPLOAD_FIELD & DOCUMENT_REQUEST)
      formValues: formFieldValues,
    };

    // Prepare metadata
    const metadata = {
      submissionType: status,
      timestamp: new Date().toISOString(),
      requestDocId: this.requestDocId,
      workflowId: this.workflowId,
      virtualModuleId: this.virtualModuleId,
      userAgent: navigator.userAgent,
      submittedBy: 'external_user', // or get from user context
    };

    // Prepare summary for logging
    const summary = {
      totalFormFields: Object.keys(formFieldValues).length,
      completedFormFields: Object.values(formFieldValues).filter(
        (value) => value !== null && value !== undefined && value !== ''
      ).length,
      totalDocuments,
      uploadedDocuments: uploadedCount,
      documentSections: Object.keys(this.documentUploadData).length,
      regularDocuments: uploadedDocuments.length,
      formFieldDocuments: formFieldDocuments.length,
      submissionType: status,
      hasFormData: Object.keys(formFieldValues).length > 0,
      hasDocuments:
        uploadedDocuments.length > 0 || formFieldDocuments.length > 0,
      isComplete: this.isFormValid(),
    };

    return {
      formData,
      metadata,
      summary,
    };
  }

  // Validate form data before submission
  validateSubmissionData(formData: {
    formValues?: Record<string, unknown>;
    documents?: unknown[];
  }): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if we have any data to submit
    const hasFormValues =
      formData.formValues && Object.keys(formData.formValues).length > 0;
    const hasDocuments = formData.documents && formData.documents.length > 0;

    if (!hasFormValues && !hasDocuments) {
      errors.push('No form data or documents to submit');
    }

    // Validate form fields
    if (hasFormValues) {
      this.getEnrichedValueData().forEach((item: any) => {
        const fieldData = item.enrichedData;
        const value = formData.formValues[fieldData.formControlName];

        if (
          fieldData.isRequired &&
          (value === null || value === undefined || value === '')
        ) {
          errors.push(`Required field "${fieldData.label}" is empty`);
        }
      });
    }

    // Validate form field documents (UPLOAD_FIELD and DOCUMENT_REQUEST)
    this.getEnrichedValueData().forEach((item) => {
      const fieldData = item.enrichedData;

      if (fieldData.type === 'UPLOAD_FIELD' && fieldData.isRequired) {
        const sectionKey = `UPLOAD_FIELD_${fieldData.formControlName}`;
        const uploadData = this.documentUploadData[sectionKey];
        const hasUploadedDoc = uploadData?.toBeUploadedDocuments?.some(
          (d) => d.status === AttachmentStatus.UPLOADED
        );
        if (!hasUploadedDoc) {
          errors.push(
            `Required upload field "${fieldData.title}" has no uploaded files`
          );
        }
      } else if (fieldData.type === 'DOCUMENT_REQUEST' && fieldData.value) {
        // For DOCUMENT_REQUEST, check if any files are uploaded for this field
        // Since the current API structure doesn't provide per-document requirements,
        // we'll check if the field itself is required
        if (fieldData.isRequired) {
          // Check if any document in this request has uploaded files
          let hasAnyUploadedDoc = false;

          // Check all document sections for this form control
          Object.keys(this.documentUploadData).forEach((sectionKey) => {
            if (
              sectionKey.startsWith(`DOC_REQ_${fieldData.formControlName}_`)
            ) {
              const uploadData = this.documentUploadData[sectionKey];
              if (
                uploadData?.toBeUploadedDocuments?.some(
                  (d) => d.status === 'UPLOADED'
                )
              ) {
                hasAnyUploadedDoc = true;
              }
            }
          });

          if (!hasAnyUploadedDoc) {
            errors.push(
              `Required document request "${fieldData.title}" has no uploaded files`
            );
          }
        }
      }
    });

    // Add warnings for incomplete data
    if (hasFormValues) {
      const emptyFields = Object.entries(formData.formValues).filter(
        ([, value]) => value === null || value === undefined || value === ''
      ).length;

      if (emptyFields > 0) {
        warnings.push(`${emptyFields} form field(s) are empty`);
      }
    }

    const isValid = errors.length === 0;

    return {
      isValid,
      errors,
      warnings,
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Drag and Drop Event Handlers
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  }

  triggerFileSelected(file: File, sectionKey: string) {
    // Check if this is a form field upload (starts with UPLOAD_FIELD_ or DOC_REQ_)
    if (
      sectionKey.startsWith('UPLOAD_FIELD_') ||
      sectionKey.startsWith('DOC_REQ_')
    ) {
      this.handleFormFieldFileUpload(file, sectionKey);
      return;
    }

    // Original logic for document sections
    const document: IAttachmentDocument = {
      name: file.name,
      uploaded_date: new Date(),
      type: 'unknown',
      status: AttachmentStatus.UPLOADING,
    };

    this.saveDocument(document, file, sectionKey);
  }

  saveDocument(document: IAttachmentDocument, file: File, sectionKey: string) {
    // Mark form as touched like in create-thread
    this.documentCollectionFormData.markAllAsTouched();
    this.documentCollectionFormData.markAsDirty();
    this.documentCollectionFormData.updateValueAndValidity();

    // Ensure section exists in documentUploadData
    if (!this.documentUploadData[sectionKey]) {
      this.documentUploadData[sectionKey] = {
        documentName: sectionKey,
        identity_id: 'current_user',
        dataList: [],
        toBeUploadedData: [],
        toBeUploadedDocuments: [],
        toBeDeleted: [],
      };
    }

    const uploadData = this.documentUploadData[sectionKey];
    const index = uploadData.dataList.length;

    // Create upload entity following create-thread pattern
    const entity: IUploadFileToDMS<File> = {
      is_public: false,
      is_system: true,
      generate_name: true,
      path: `RequestDocuments/${this.requestDocId}/${sectionKey}`,
      tags: ['RequestDocuments', sectionKey],
      file,
    };

    // Create document with proper status like create-thread
    const savedFile: IAttachmentDocument = {
      ...document,
      status: AttachmentStatus.PENDING_UPLOAD,
      file_id: crypto.randomUUID(),
    };

    this.updateDataListTableElement(index, savedFile, sectionKey);
    uploadData.toBeUploadedData.push(entity);
    uploadData.toBeUploadedDocuments.push(savedFile);
  }

  private changeLoadingState(
    index: number,
    is_loading: boolean,
    documentName: string
  ) {
    const uploadData = this.documentUploadData[documentName];
    if (uploadData?.dataList[index]) {
      uploadData.dataList[index].loading = is_loading;
      uploadData.dataList[index].expanded = !is_loading;
    }
  }

  action(
    e: { action: string; data: any; index: number },
    documentName: string
  ) {
    switch (e.action) {
      case ACTIONS.DELETE:
        this.deleteDocument(e.index, e.data, documentName);
        break;
      case ACTIONS.DOWNLOAD:
        this.downloadDocument(e.index, e.data, documentName);
        break;
      default:
        break;
    }
  }

  deleteDocument(
    index: number,
    document: IAttachmentDocument,
    sectionKey: string
  ) {
    const uploadData = this.documentUploadData[sectionKey];
    if (!uploadData) return;

    if (document.status === AttachmentStatus.UPLOADING) {
      return;
    }

    if (document.status === AttachmentStatus.PENDING_UPLOAD) {
      uploadData.dataList.splice(index, 1);
      uploadData.toBeUploadedDocuments.splice(index, 1);
      return;
    }

    if (document.status === AttachmentStatus.FAILED) {
      uploadData.dataList.splice(index, 1);
      uploadData.toBeUploadedDocuments.splice(index, 1);
      return;
    }

    if (
      document.status === AttachmentStatus.UPLOADED ||
      document.status === AttachmentStatus.RETURNED
    ) {
      uploadData.toBeDeleted.push(document);
      const savedFile: IAttachmentDocument = {
        ...document,
        status: AttachmentStatus.PENDING_DELETE,
      };
      this.updateDataListTableElement(index, savedFile, sectionKey);
    }
  }

  private downloadDocument(index: number, document: any, documentName: string) {
    this.changeLoadingState(index, true, documentName);
    this.filesService
      .downloadEntityFromDMS(document.file_id, document.name)
      .then(() => {
        this.changeLoadingState(index, false, documentName);
        this.snackbar.success(
          SUCCESS_TYPES.DOWNLOADED,
          this.translate.instant('knowledgebase.file')
        );
      })
      .catch(() => {
        this.changeLoadingState(index, false, documentName);
        this.snackbar.error(
          this.translate.instant('knowledgebase.document.download-failed')
        );
      });
  }

  async updateDataListTableElement(
    index: number,
    document: any,
    documentName: string
  ) {
    const uploadData = this.documentUploadData[documentName];
    if (!uploadData) return;

    const dataElements = [];
    const actionElements = [];

    dataElements.push(
      {
        key: 'Document Name',
        value: document.name,
      },
      this.getStatusChip(document)
    );
    if (document.status !== 'PENDING_UPLOAD') {
      dataElements.push({
        key: 'Uploaded Date(Time)',
        value: document.uploaded_date
          ? new Date(document.uploaded_date).toLocaleDateString()
          : '',
      });
    }

    switch (document.status) {
      case AttachmentStatus.PENDING_UPLOAD:
        actionElements.push(this.createDeleteAction(document));
        break;
      case AttachmentStatus.FAILED:
        actionElements.push(this.createDeleteAction(document));
        break;
      case AttachmentStatus.REJECTED:
      case AttachmentStatus.RETURNED:
        actionElements.push(this.createDeleteAction(document));
        actionElements.push(this.createDownloadAction(document));
        break;
      case AttachmentStatus.UPLOADED:
      case AttachmentStatus.PENDING_DELETE:
      case AttachmentStatus.APPROVED:
        actionElements.push(this.createDownloadAction(document));
        break;
    }

    uploadData.dataList[index] = {
      loading: document.status === 'UPLOADING',
      expanded: document.status === 'FAILED',
      data: dataElements,
      icon: 'insert_drive_file',
      actions: actionElements,
      index,
    };

    uploadData.toBeUploadedDocuments[index] = document;
  }

  private createDownloadAction(document: any) {
    return {
      icon: 'save_alt',
      face: this.translate.instant('messages.download'),
      tooltip: this.translate.instant('messages.download'),
      action: ACTIONS.DOWNLOAD,
      data: document,
    };
  }

  private createDeleteAction(document: any) {
    return {
      icon: 'delete',
      face: '',
      tooltip: this.translate.instant('messages.delete'),
      action: ACTIONS.DELETE,
      data: document,
    };
  }

  getStatusChip(document: IAttachmentDocument) {
    let value;
    let chipBackgroundColor;

    switch (document.status) {
      case AttachmentStatus.UPLOADED:
        value = this.translate.instant('messages.document.uploaded');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.UPLOADED;
        break;
      case AttachmentStatus.APPROVED:
        value = this.translate.instant('messages.document.approved');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.APPROVED;
        break;
      case AttachmentStatus.REJECTED:
        value = this.translate.instant('messages.document.rejected');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.REJECTED;
        break;
      case AttachmentStatus.RETURNED:
        value = this.translate.instant('messages.document.returned');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.RETURNED;
        break;
      case AttachmentStatus.FAILED:
        value = this.translate.instant('messages.document.failed');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.FAILED;
        break;
      case AttachmentStatus.UPLOADING:
        value = this.translate.instant('messages.document.uploading');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.UPLOADING;
        break;
      case AttachmentStatus.PENDING_DELETE:
        value = this.translate.instant('messages.document.pending-delete');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.PENDING_DELETE;
        break;
      case AttachmentStatus.PENDING_UPLOAD:
        value = this.translate.instant('messages.document.pending-upload');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.PENDING_UPLOAD;
        break;
      default:
        value = 'Unknown';
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.FAILED;
        break;
    }

    return {
      key: this.translate.instant('messages.document.status'),
      value,
      type: DLT_DATA_TYPES.CHIP,
      chipBackgroundColor,
      chipColor: 'white',
    };
  }

  // async uploadFilesToDMS(sectionKey: string): Promise<void> {
  //   this.isLoading = true;

  //   try {
  //     if (this.toBeUploadedData?.length) {
  //       await this.uploadDocument(sectionKey);
  //     }

  //     if (this.toBeDeletedData?.length) {
  //       await this.deleteFiles();
  //     }
  //   } catch {
  //     this.snackbar.error(
  //       this.translate.instant('messages.document.upload-failed')
  //     );
  //   } finally {
  //     this.isLoading = false;
  //   }
  // }

  async deleteFiles(): Promise<void> {
    if (!this.toBeDeletedData?.length) return;

    this.isLoading = true;

    for (const data of this.toBeDeletedData) {
      try {
        await new Promise<void>((resolve, reject) => {
          this.filesService.deleteEntityFromDMS(data.file_id).subscribe({
            next: () => resolve(),
            error: (e) =>
              reject(
                new Error(
                  `Failed to delete file: ${e?.message || 'Unknown error'}`
                )
              ),
          });
        });
      } catch {
        // Silently continue with other files if one fails
      }
    }

    this.isLoading = false;
  }

  async uploadDocument(sectionKey: string) {
    const section = this.documentUploadData[sectionKey];
    if (!section?.toBeUploadedData.length) return;

    let uploadDataLength = section.toBeUploadedData.length;

    for (const uploadData of section.toBeUploadedData) {
      this.isLoading = true;
      const index = section.dataList.length - uploadDataLength--;

      await new Promise<void>((resolve, reject) => {
        this.filesService
          .uploadFileToDMS(
            uploadData,
            undefined,
            this.externalToken || undefined
          )
          .subscribe({
            next: (res) => {
              this.isLoading = false;

              const file: IAttachmentDocument = {
                type: res.data.type,
                status: AttachmentStatus.UPLOADED,
                file_id: res.data._id.toString(),
                name: uploadData.file.name,
                uploaded_date: res.data.last_modified_on,
              };

              this.updateDataListTableElement(index, file, sectionKey);
              // this.snackbar.success(SUCCESS_TYPES.SAVED, file.name);
              resolve();
            },
            error: () => {
              this.isLoading = false;
              const file: IAttachmentDocument = {
                status: AttachmentStatus.FAILED,
                name: uploadData.file.name,
                type: '',
                uploaded_date: undefined,
              };

              this.updateDataListTableElement(index, file, sectionKey);
              this.snackbar.error(
                this.translate.instant('messages.document.upload-failed')
              );
              reject(
                new Error(
                  this.translate.instant('messages.document.upload-failed')
                )
              );
            },
          });
      });
    }
  }

  async onSubmit(status: string) {
    this.isSubmitting = true;
    // Update form controls state when submitting
    this.updateFormControlsState();

    try {
      // Step 1: Handle document uploads for all sections (including form fields)
      for (const sectionKey of Object.keys(this.documentUploadData)) {
        const section = this.documentUploadData[sectionKey];

        this.toBeUploadedData = section.toBeUploadedData || [];
        this.toBeDeletedData = section.toBeDeleted || [];

        if (this.toBeUploadedData.length) {
          await this.uploadDocument(sectionKey);
        }

        if (this.toBeDeletedData.length) {
          await this.deleteFiles();
        }
      }

      // Step 2: Prepare complete submission data
      const submissionData = this.prepareSubmissionData(status);

      // Step 3: Validate the prepared data
      const validation = this.validateSubmissionData(submissionData.formData);

      if (!validation.isValid) {
        this.snackbar.error('Validation Error', validation.errors.join(', '));
        this.isSubmitting = false;
        return;
      }

      // Step 4: Submit the form data to the API
      await this.submitFormDataToServer(submissionData.formData, status);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to submit form data';
      this.snackbar.error('Error', errorMessage);
    } finally {
      this.isSubmitting = false;
      // Update form controls state after submission completes
      this.updateFormControlsState();
    }
  }

  // Extract API submission logic to separate method
  private submitFormDataToServer(
    formData: {
      formValues?: Record<string, unknown>;
      documents?: any[];
      formFieldDocuments?: any[];
    },
    status: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.virtualModuleFormBuilderService
        .submitFormData(
          this.virtualModuleId,
          formData,
          status as 'save' | 'draft'
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.success) {
              const successMessage =
                status === 'save' ? 'Form submitted' : 'Form drafted';

              // Update local status after successful submission
              if (status === 'save' && this.virtualModuleData) {
                this.virtualModuleData.status =
                  STAGE_ROUTING_STATUS.REQUEST_SUBMITTED;
              }

              this.snackbar.success(SUCCESS_TYPES.CUSTOM, successMessage);
              resolve();
            } else {
              reject(new Error(response?.message || 'Unknown error occurred'));
            }
          },
          error: (error) => {
            console.error('API submission error:', error);
            reject(new Error(error?.message || 'Failed to submit form data'));
          },
        });
    });
  }

  isFormValid(): boolean {
    // Check if at least one document is uploaded for each required document type
    let isValid = true;

    // Also validate form field requirements
    this.getEnrichedValueData().forEach((item) => {
      const fieldData = item.enrichedData;

      if (fieldData.isRequired) {
        if (fieldData.type === 'UPLOAD_FIELD') {
          const sectionKey = `UPLOAD_FIELD_${fieldData.formControlName}`;
          const uploadData = this.documentUploadData[sectionKey];
          const hasUploadedDoc = uploadData?.toBeUploadedDocuments?.some(
            (d) => d.status === 'UPLOADED'
          );
          if (!hasUploadedDoc) {
            isValid = false;
          }
        } else if (fieldData.type === 'DOCUMENT_REQUEST' && fieldData.value) {
          // Check if any document in this request has uploaded files
          let hasAnyUploadedDoc = false;
          Object.keys(this.documentUploadData).forEach((sectionKey) => {
            if (
              sectionKey.startsWith(`DOC_REQ_${fieldData.formControlName}_`)
            ) {
              const uploadData = this.documentUploadData[sectionKey];
              if (
                uploadData?.toBeUploadedDocuments?.some(
                  (d) => d.status === 'UPLOADED'
                )
              ) {
                hasAnyUploadedDoc = true;
              }
            }
          });
          if (!hasAnyUploadedDoc) {
            isValid = false;
          }
        } else {
          // For other field types, check if form control has a value
          const value = item.formControl?.value;
          if (value === null || value === undefined || value === '') {
            isValid = false;
          }
        }
      }
    });

    return isValid;
  }

  get areButtonsDisabled(): boolean {
    return (
      this.isSubmitting ||
      this.virtualModuleData?.status ===
        STAGE_ROUTING_STATUS.REQUEST_SUBMITTED ||
      this.virtualModuleData?.status === STAGE_ROUTING_STATUS.APPROVED ||
      this.virtualModuleData?.status === STAGE_ROUTING_STATUS.REJECTED
    );
  }

  // Check if all required documents are uploaded
  areRequiredDocumentsUploaded(): boolean {
    const enrichedData = this.getEnrichedValueData();

    for (const item of enrichedData) {
      // Check DOCUMENT_REQUEST fields
      if (item.enrichedData.type === 'DOCUMENT_REQUEST') {
        const documents = this.getDocumentRequestDocuments(item.enrichedData);

        for (let docIndex = 0; docIndex < documents.length; docIndex++) {
          const document = documents[docIndex];

          // If document is required, check if it's uploaded
          if (document.isRequired) {
            const sectionKey = `DOC_REQ_${item.enrichedData.formControlName}_${docIndex}`;
            const uploadData = this.documentUploadData[sectionKey];

            // Check if there's at least one uploaded document (UPLOADED or APPROVED status)
            // Also allow PENDING_UPLOAD as files are uploaded during submission
            const hasUploadedDoc = uploadData?.toBeUploadedDocuments?.some(
              (doc) =>
                doc.status === AttachmentStatus.UPLOADED ||
                doc.status === AttachmentStatus.APPROVED ||
                doc.status === AttachmentStatus.PENDING_UPLOAD
            );

            if (!hasUploadedDoc) {
              return false; // Required document is not uploaded
            }
          }
        }
      }

      // Check UPLOAD_FIELD fields
      if (
        item.enrichedData.type === 'UPLOAD_FIELD' &&
        item.enrichedData.isRequired
      ) {
        const sectionKey = `UPLOAD_FIELD_${item.enrichedData.formControlName}`;
        const uploadData = this.documentUploadData[sectionKey];

        // Check if there's at least one uploaded document (UPLOADED or APPROVED status)
        // Also allow PENDING_UPLOAD as files are uploaded during submission
        const hasUploadedDoc = uploadData?.toBeUploadedDocuments?.some(
          (doc) =>
            doc.status === AttachmentStatus.UPLOADED ||
            doc.status === AttachmentStatus.APPROVED ||
            doc.status === AttachmentStatus.PENDING_UPLOAD
        );

        if (!hasUploadedDoc) {
          return false; // Required upload field has no uploaded document
        }
      }
    }

    return true; // All required documents are uploaded
  }

  // Getter for Save button disabled state (includes required documents check)
  get isSaveButtonDisabled(): boolean {
    return this.areButtonsDisabled || !this.areRequiredDocumentsUploaded();
  }

  // Check if a document is already uploaded for a given section key
  isDocumentUploaded(sectionKey: string): boolean {
    const uploadData = this.documentUploadData[sectionKey];
    if (!uploadData) {
      return false;
    }
    // Check if there's at least one document (uploaded, pending upload, or in other valid states)
    // Exclude only FAILED and PENDING_DELETE statuses
    return (
      uploadData.toBeUploadedDocuments?.some(
        (doc) =>
          doc.status === AttachmentStatus.UPLOADED ||
          doc.status === AttachmentStatus.APPROVED ||
          doc.status === AttachmentStatus.REJECTED ||
          doc.status === AttachmentStatus.RETURNED ||
          doc.status === AttachmentStatus.PENDING_UPLOAD ||
          doc.status === AttachmentStatus.UPLOADING
      ) || false
    );
  }

  // Check if drag and drop should be enabled for a document
  shouldAllowUpload(document: any, sectionKey: string): boolean {
    // Disable drag and drop if buttons are disabled
    if (this.areButtonsDisabled) {
      return false;
    }

    const uploadData = this.documentUploadData[sectionKey];
    if (!uploadData || !uploadData.toBeUploadedDocuments) {
      return true; // Enable if no data exists yet
    }

    const documents = uploadData.toBeUploadedDocuments;
    const isMultipleUpload = document.isMultipleUpload;

    // If multiple upload is allowed
    if (isMultipleUpload) {
      // Disable if ALL documents are approved
      if (documents.length === 0) {
        return true; // Enable if no documents yet
      }
      const allApproved = documents.every(
        (doc) => doc.status === AttachmentStatus.APPROVED
      );
      return !allApproved; // Disable if all are approved
    }

    // If single upload
    // Disable if the document is approved
    const hasApproved = documents.some(
      (doc) => doc.status === AttachmentStatus.APPROVED
    );
    if (hasApproved) {
      return false; // Disable if document is approved
    }

    // If single upload and document is already uploaded (but not approved), disable
    if (this.isDocumentUploaded(sectionKey)) {
      return false;
    }

    // Otherwise, enable
    return true;
  }
}
